import React, { useEffect } from "react";
import { Clusterer, Placemark } from "react-yandex-maps";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import YandexMap from "src/components/common/YandexMap/YandexMap";
import { useYMaps } from "src/components/common/YandexMap/hooks/useYMaps";
import { ReservationYMapProps } from "src/components/Reservation/types";
import Logo from "src/../public/assets/icons/logo.png";
import Image from "next/image";
import { Typography } from "@mui/material";
import { colors } from "src/config/colors";
import { AddressPlacemarkState } from "src/components/ReservationProduct/ReservationProduct";
import MapReservationPanel from "src/components/ReservationProduct/components/MapReservationPanel";
import BottomReservationPanel from "src/components/ReservationProduct/components/BottomReservationPanel";
import LoadBackdrop from "src/components/Reservation/components/ReservationYMap/LoadBackdrop";

const ColorsCode = {
  1: "filled", // обычная метка
  2: "underTheOrder", // под заказ
  3: "underTheOrder", // забронировать
  4: "priority", // приоритетный оффер
};

const ReservationYMap: React.FC<ReservationYMapProps> = ({
  pharmacyHandler,
  selectedPharmacy,
  coords,
  setCoords,
  isOpenMap,
  setOpenReservationPanel,
  isOpenReservationPanel,
  addressPlacemark,
  setAddressPlacemark,
  reservationProductHandler,
  imgSource,
  setOpenDetailModal,
  filtersData,
  productSlug,
  dataMapOffers,
  placemarkReady,
  handlePlacemarkLoad,
}) => {
  const selectedMapOfferItems = dataMapOffers?.[selectedPharmacy]?.items;

  const { mapApi, setMapApi, template } = useYMaps();

  const onClickPlacemark = (index: number) => {
    pharmacyHandler(index);
    setOpenReservationPanel(true);

    if (dataMapOffers && dataMapOffers[index]) {
      setCoords([
        dataMapOffers[index].longitude,
        dataMapOffers[index].latitude,
      ]);
    }
    setAddressPlacemark(AddressPlacemarkState.hide);
  };

  useEffect(() => {
    if (dataMapOffers && dataMapOffers.length > 0) {
      setCoords([dataMapOffers[0].longitude, dataMapOffers[0].latitude]);
    }
  }, [dataMapOffers]);

  return (
    <Box
      sx={{
        position: "relative",
        minHeight: "500px",
      }}
    >
      {mapApi && <LoadBackdrop isLoading={!placemarkReady} />}
      <Box
        display="flex"
        alignItems="center"
        minHeight={{ xs: "calc(100vh)", md: "calc(100vh - 72px)" }}
        justifyContent="center"
        sx={{
          position: "absolute",
          left: "50%",
          transform: "translateX(-50%)",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Box
          sx={{
            position: "relative",
          }}
        >
          <CircularProgress size={80} />
          <Box
            sx={{
              position: "absolute",
              left: "50%",
              top: "30%",
              transform: "translateX(-50%)",
            }}
          >
            <Image alt="Лого" width="45px" height="38px" src={Logo} />
          </Box>
        </Box>
        <Typography
          component="span"
          sx={{
            fontSize: "13px",
            whiteSpace: "nowrap",
            fontWeight: 700,
            color: colors.blue,
            textAlign: "center",
          }}
        >
          Идёт поиск предложений.
          <br />
          Обычно это занимает не более 10-15 секунд.
          <br />
          Хорошего вам дня &#128522;
        </Typography>
      </Box>
      {dataMapOffers && (
        <YandexMap
          addressPlacemark={addressPlacemark}
          coords={coords}
          setYmapsApi={setMapApi}
        >
          {addressPlacemark === "show" && coords && (
            <Placemark
              geometry={coords}
              options={{
                preset: "island#icon",
              }}
            />
          )}
          {mapApi && (
            <Clusterer
              options={{
                preset: "islands#invertedNightClusterIcons",
                groupByCoordinates: false,
                gridSize: 128,
                zoomMargin: 100,
              }}
            >
              {dataMapOffers &&
                dataMapOffers.map((value, index) => {
                  return selectedPharmacy !== index ? (
                    <Placemark
                      key={value.latitude + index}
                      geometry={[value?.longitude, value?.latitude]}
                      properties={{
                        iconContent: value.min_price + " ₽",
                      }}
                      onClick={() => onClickPlacemark(index)}
                      options={{
                        iconLayout: "default#imageWithContent",
                        iconImageHref: "",
                        iconContentLayout: template(
                          "#fff",
                          ColorsCode[value.color_code]
                        ),
                        iconContentOffset: [-31, -42],
                        iconShape: {
                          type: "Rectangle",
                          // The rectangle is defined as two points: the upper left and lower right.
                          coordinates: [
                            [-32, -42],
                            [32, 0],
                          ],
                        },
                      }}
                    />
                  ) : (
                    <Placemark
                      key={value.latitude + index}
                      geometry={[value?.longitude, value?.latitude]}
                      properties={{
                        iconContent: value.min_price + " ₽",
                      }}
                      onClick={() => setOpenReservationPanel(true)}
                      onLoad={handlePlacemarkLoad}
                      options={{
                        iconLayout: "default#imageWithContent",
                        iconImageHref: "",
                        iconContentLayout: template(
                          isOpenReservationPanel ? "000" : "#fff",
                          isOpenReservationPanel
                            ? "active"
                            : ColorsCode[value.color_code]
                        ),
                        iconContentOffset: [-31, -42],
                        iconShape: {
                          type: "Rectangle",
                          // The rectangle is defined as two points: the upper left and lower right.
                          coordinates: [
                            [-32, -42],
                            [32, 0],
                          ],
                        },
                      }}
                    />
                  );
                })}
            </Clusterer>
          )}
        </YandexMap>
      )}
      {selectedMapOfferItems && (
        <>
          <MapReservationPanel
            toBookClick={reservationProductHandler}
            imgSource={imgSource}
            setOpenDetailModal={setOpenDetailModal}
            dataSelectedMapOffers={selectedMapOfferItems}
            isOpenReservationPanel={isOpenReservationPanel}
            setOpenReservationPanel={setOpenReservationPanel}
          />
          <BottomReservationPanel
            toBookClick={reservationProductHandler}
            imgSource={imgSource}
            setOpenDetailModal={setOpenDetailModal}
            dataSelectedMapOffers={selectedMapOfferItems}
            isOpen={isOpenReservationPanel}
            onClose={() => setOpenReservationPanel(false)}
          />
        </>
      )}
    </Box>
  );
};

export default ReservationYMap;
